import React, { useEffect, useState } from 'react';
import { App, Modal, Button, Spin, Typography, Descriptions } from 'antd';
import { FileOutlined, FolderOutlined, UserOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getShareInfo, acceptShare, rejectShare, ShareInfoResponse } from '@/api/share';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

interface AcceptShareDialogProps {
    visible: boolean;
    shareId: string;
    onClose: () => void;
    onSuccess?: (filePath: string) => void;
}

const AcceptShareDialog: React.FC<AcceptShareDialogProps> = ({ visible, shareId, onClose, onSuccess }) => {
    const { message } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [shareInfo, setShareInfo] = useState<ShareInfoResponse | null>(null);
    const { t } = useTranslation('common');

    useEffect(() => {
        if (visible && shareId) {
            loadShareInfo();
        }
    }, [visible, shareId]);

    const loadShareInfo = async () => {
        setLoading(true);
        try {
            const response = await getShareInfo({ shareId });
            if (response?.ok) {
                setShareInfo(response.data);
            } else {
                message.error(response?.message || t('accept_share.load_failed'));
                onClose();
            }
        } catch (e) {
            console.error('Failed to load share info', e);
            message.error(t('accept_share.load_failed'));
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        setSubmitting(true);
        try {
            const response = await acceptShare({ shareId });
            if (response?.ok && response.data) {
                message.success(t('accept_share.success'));
                onSuccess?.(response.data.filePath);
                onClose();
            } else {
                message.error(response?.message || t('accept_share.failed'));
            }
        } catch (e) {
            console.error('Failed to accept share', e);
            message.error(t('accept_share.failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        setSubmitting(true);
        try {
            const response = await rejectShare({ shareId });
            if (response?.ok) {
                message.success(t('accept_share.reject_success', { defaultValue: 'Share invitation rejected' }));
                onClose();
            } else {
                message.error(response?.message || t('accept_share.reject_failed', { defaultValue: 'Failed to reject share invitation' }));
            }
        } catch (e) {
            console.error('Failed to reject share', e);
            message.error(t('accept_share.reject_failed', { defaultValue: 'Failed to reject share invitation' }));
        } finally {
            setSubmitting(false);
        }
    };

    const isFolder = shareInfo?.file_path.endsWith('/');
    const icon = isFolder ? <FolderOutlined style={{ fontSize: 48, color: '#1890ff' }} /> : <FileOutlined style={{ fontSize: 48, color: '#52c41a' }} />;

    return (
        <Modal
            title={<Title level={4}>{t(isFolder ? 'accept_share.title_folder' : 'accept_share.title_file')}</Title>}
            open={visible}
            onCancel={onClose}
            closable={false}
            width={500}
            footer={
               shareInfo&&shareInfo.status == "pending"?
                <div className="flex justify-end space-x-2">
                    <Button onClick={onClose} disabled={submitting}>
                        {t('accept_share.cancel')}
                    </Button>
                    <Button
                        danger
                        onClick={handleReject}
                        loading={submitting}
                        disabled={loading || !shareInfo}
                    >
                        {t('accept_share.reject', { defaultValue: 'Reject' })}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleAccept}
                        loading={submitting}
                        disabled={loading || !shareInfo}
                    >
                        {t('accept_share.accept')}
                    </Button>
                </div>
                :<></>
            }
        >
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <Spin size="large" tip={t('accept_share.loading')} />
                </div>
            ) : shareInfo&&shareInfo.status == "pending" ? (
                <div className="space-y-4">
                    <div className="flex items-center justify-center py-6">
                        {icon}
                    </div>
                    
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label={<Text strong>{t(isFolder ? 'accept_share.folder_name' : 'accept_share.file_name')}</Text>}>
                            <Text>{shareInfo.file_path}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text strong><UserOutlined /> {t('accept_share.shared_by')}</Text>}>
                            <Text>{shareInfo.sharing_user}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<Text strong><ClockCircleOutlined /> {t('accept_share.shared_at')}</Text>}>
                            <Text>{new Date(shareInfo.created_at).toLocaleString()}</Text>
                        </Descriptions.Item>
                    </Descriptions>

                    <div className="p-3 bg-blue-50 dark:bg-[#1E3A5F] rounded border border-blue-200 dark:border-[#0E639C]">
                        <Text className="text-sm text-gray-700 dark:text-[#CCCCCC]">
                            {t(isFolder ? 'accept_share.accept_info_folder' : 'accept_share.accept_info_file')}
                        </Text>
                    </div>
                </div>
            ) : (
                <div className="text-center pt-6">
                    <Text>
                        {t('accept_share.invalid_link')}
                    </Text>
                </div>
            )}
        </Modal>
    );
};

export default AcceptShareDialog;
